import { Component, ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

/**
 * Catches failed dynamic import (chunk load) errors and shows a reload prompt
 * instead of a white screen. A chunk fetch can fail due to a network blip or
 * a new deployment invalidating the old content-hashed filename.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    const isChunkError =
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.name === "ChunkLoadError";
    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: Error) {
    console.error("[ChunkErrorBoundary]", error.message);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        gap={2}
      >
        <Typography variant="h6" color="text.secondary">
          {this.state.isChunkError
            ? "A new version is available. Please reload to continue."
            : "Something went wrong loading this page."}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Box>
    );
  }
}
